import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  inject,
  Injector,
  Optional,
  QueryList,
  TemplateRef
} from '@angular/core';
import { CommonModule } from "@angular/common";

import {
  APPLY_STYLES_TYPE,
  DISPLAY_TYPE,
  IWidgetConfig,
  Parent,
  provideAsWidgetRef,
  register,
  StylableComponent,
  styler
} from "@wm/components-base";
import { WizardstepComponent } from "./wizardstep/wizardstep.component";
import { WizardActionDirective } from './wizardaction/wizardaction.directive';
import { WmsTranscludeDirective } from "../../page/wms-transclude/wms-transclude.directive";
import { runtimeEventManager } from "@wm/core";
import { includes } from "lodash-es";

const DEFAULT_CLS = 'app-wizard panel clearfix';
const WIDGET_CONFIG: IWidgetConfig = {
  widgetType: 'wm-wizard',
  hostClass: DEFAULT_CLS,
  displayType: DISPLAY_TYPE.INLINE_BLOCK
};

@Component({
  selector: 'div[wmWizard]',
  templateUrl: './wizard.component.html',
  providers: [
    provideAsWidgetRef(WizardComponent),
    WizardstepComponent,
    WizardActionDirective
  ],
  exportAs: 'wmWizard',
  standalone: true,
  imports: [CommonModule, WmsTranscludeDirective],
  styles: []
})
export class WizardComponent extends StylableComponent implements AfterViewInit, AfterContentInit {
  static initializeProps = register('wm-wizard');

  @ContentChildren(WizardstepComponent) steps: QueryList<WizardstepComponent>;
  @ContentChild('wizardAction', { read: TemplateRef, descendants:false }) wizardAction: TemplateRef<any>;
  public currentStep: WizardstepComponent;
  public stepClass: string;
  public class: string;
  public actionsalignment: any;
  public cancelable: any;
  public name: string;
  public cancelbtnlabel: any;
  public previousbtnlabel: any;
  public nextbtnlabel: any;
  public donebtnlabel: any;
  public isRNapp: boolean;
  public type: any;
  public doneButtonIconClass: string;

  get hasPrevStep(): boolean {
    return !this.isFirstStep(this.currentStep);
  }

  get hasNextStep(): boolean {
    return !this.isLastStep(this.currentStep);
  }

  get showDoneBtn() {
    if (!this.currentStep) {
      return;
    }
    return !this.hasNextStep && this.currentStep.enableDone;
  }

  constructor(inj: Injector, @Optional() viewParent: Parent, private cdr: ChangeDetectorRef) {
    super(inj, WIDGET_CONFIG, viewParent);
    styler(this.nativeElement, this, APPLY_STYLES_TYPE.SHELL);
    const projectService = runtimeEventManager.getService('projectService');
    this.isRNapp = projectService.isNativeMobileApplicationType();
  }

  addWizardStep(wizardStep?: any) {
    if (this.steps && !includes(this.steps['_results'], wizardStep)) {
      this.steps?.reset([...this.steps.toArray(), wizardStep])
    }
    this.updateStepList();
  }

  override onPropertyChange(key: string, nv: any, ov?: any) {
    if (key === 'stepstyle') {
      this.stepClass = nv === 'justified' ? 'nav-justified' : '';
    } else if (key === 'type') {
      this.setProperties(nv, ov);
    } else {
      super.onPropertyChange(key, nv, ov);
    }
    if(key==='donebtniconclass'){
      this.doneButtonIconClass = nv;
    }
  }

  getStepIndexByRef(wizardStep: any): number {
    return this.steps.toArray().indexOf(wizardStep);
  }

  getStepRefByIndex(index: number) {
    return this.steps?.toArray()[index];
  }

  onWizardHeaderClick($event: Event, currentStep: WizardstepComponent) {
    $event.stopPropagation();
    runtimeEventManager.emit('selectWizardStep', { widgetId: currentStep.widgetId })
    // set the selected step as current step and make it active
    this.steps.forEach((step: any, index) => {
      if (index == this.getStepIndexByRef(currentStep)) {
        this.currentStep = currentStep;
        this.currentStep.isActive = true;
      } else {
        step.disabled = true;
      }
    })
  }

  override ngAfterViewInit() {
    styler(this.nativeElement.querySelector('.panel-body') as HTMLElement, this, APPLY_STYLES_TYPE.INNER_SHELL);
  }

  ngAfterContentInit() {
    if (!this.currentStep && this.getStepRefByIndex(0)) {
      this.currentStep = this.getStepRefByIndex(0);
      this.currentStep.isActive = true;
    }
    setTimeout(() => {
      if (this.steps.length && this.type === 'dynamic' && this.getStepRefByIndex(0)) {
        this.onWizardHeaderClick(new Event('click'), this.getStepRefByIndex(0));
      }
    });
    this.updateStepList();

  }

  updateStepList() {
    let options = [{ 'label': 'None', 'value': 'none' }];
    this.steps.forEach((step: any) => {
      options.push({ 'label': step.name, 'value': step.name });
    })
    this.widgetProps['defaultstep'].options = options;
  }

  isFirstStep(stepRef: WizardstepComponent) {
    return this.steps.first === stepRef;
  }

  isLastStep(stepRef: WizardstepComponent) {
    return this.steps.last === stepRef;
  }

  renderTemplate(type: string) {
    let markup: string;
    if (type === 'dynamic') {
      markup = '<wm-wizard type="dynamic" stepstyle="justified" class="number"><wm-repeat-template>' +
        '                    <wm-wizardstep title="Step title" ></wm-wizardstep>' +
        '                </wm-repeat-template>' +
        '                <wm-wizardaction>' +
        '                    <wm-anchor class="app-wizard-skip" caption="Skip »" show="bind:skippable()" on-click="skip()"></wm-anchor>' +
        '                    <wm-container class="app-wizard-actions-right">' +
        '                        <wm-button class="btn-default app-wizard-cancel" type="button" caption="bind:cancelbtnlabel()"' +
        '                            show="bind:cancelable()" on-click="cancel()"></wm-button>' +
        '                        <wm-button class="btn-default app-wizard-previous" type="button" iconclass="wi wi-chevron-left"' +
        '                            caption="bind:previousbtnlabel()" show="bind:hasPreviousStep()" on-click="previous()"' +
        '                            disabled="bind:disablePrevious()"></wm-button>' +
        '                        <wm-button class="btn-primary app-wizard-next" type="button" iconclass="wi wi-chevron-right"' +
        '                            iconposition="right" caption="bind:nextbtnlabel()" show="bind:hasNextStep()"' +
        '                            on-click="next()" disabled="bind:disableNext()"></wm-button>' +
        '                        <wm-button class="btn-success app-wizard-done" type="button" iconclass="wi wi-check"' +
        '                            caption="bind:donebtnlabel()" show="bind:hasNoNextStep()" on-click="done()"' +
        '                            disabled="bind:disableDone()"></wm-button>' +
        '                    </wm-container>' +
        '                </wm-wizardaction></wm-wizard>';
    } else {
      markup = "<wm-wizard type='static' stepstyle='justified' class='number'>" +
        "<wm-wizardstep></wm-wizardstep>" +
        "<wm-wizardstep></wm-wizardstep>" +
        "<wm-wizardstep></wm-wizardstep>" +
        "<wm-wizardaction>" +
        "<wm-anchor class='app-wizard-skip' caption='Skip &raquo;' show='bind:skippable()' on-click='skip()'></wm-anchor>" +
        "<wm-container class='app-wizard-actions-right'>" +
        "<wm-button class='btn-default app-wizard-cancel' type='button' caption='bind:cancelbtnlabel()' show='bind:cancelable()' on-click='cancel()'></wm-button>" +
        "<wm-button class='btn-default app-wizard-previous' type='button' iconclass='wi wi-chevron-left' caption='bind:previousbtnlabel()' show='bind:hasPreviousStep()' on-click='previous()' disabled='bind:disablePrevious()'></wm-button>" +
        "<wm-button class='btn-primary app-wizard-next' type='button' iconclass='wi wi-chevron-right' iconposition='right' caption='bind:nextbtnlabel()' show='bind:hasNextStep()' on-click='next()' disabled='bind:disableNext()'></wm-button>" +
        "<wm-button class='btn-success app-wizard-done' type='button' iconclass='wi wi-check' caption='bind:donebtnlabel()' show='bind:hasNoNextStep()' on-click='done()' disabled='bind:disableDone()'></wm-button>" +
        "</wm-container>" +
        "</wm-wizardaction>" +
        "</wm-wizard>";
    }

    runtimeEventManager.emit('canvas-switch-template', {widgetId: this.widgetId, markup: markup});
  }

  setProperties(nv: string, ov?: string) {
    if (nv && ov && nv !== ov) {
      this.renderTemplate(nv);
    }
    this.widgetProps['addchild'].show = nv !== 'dynamic';
    this.widgetProps['nodatamessage'].show = nv === 'dynamic';
    this.widgetProps['dataset'].show = nv === 'dynamic';
    this.widgetProps['defaultstepindex'].show = nv === 'dynamic';
    this.widgetProps['defaultstep'].show = nv !== 'dynamic';
  }
}

